import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Info, ShieldAlert, X } from "lucide-react";
import LocationIconNew from "../../shared/icons/LocationIconNew";
import styles from "./InsuranceTerritoriesModal.module.scss";

const territoryKeys = [
  "russia",
  "ukraine",
  "israel",
  "palestinianTerritories",
  "iran",
  "lebanon",
  "syria",
  "yemen",
  "iraq",
  "myanmar",
  "afghanistan",
  "pakistan",
];

export default function InsuranceTerritoriesModal({ onClose }) {
  const { t } = useTranslation();
  const dialog = useRef(null);
  const titleId = useId();
  const descriptionId = useId();
  const listTitleId = useId();

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.querySelector("button")?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }
    if (event.key !== "Tab") return;
    const buttons = dialog.current.querySelectorAll("button");
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        ref={dialog}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.header}>
          <span className={styles.shield}>
            <ShieldAlert size={32} strokeWidth={1.7} aria-hidden="true" />
          </span>
          <h2 id={titleId}>{t("cargoInsurance.territories.title")}</h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label={t("common.close")}>
            <X size={24} aria-hidden="true" />
          </button>
        </div>
        <p id={descriptionId} className={styles.description}>
          {t("cargoInsurance.territories.description")}
        </p>
        <p id={listTitleId} className={styles.listTitle}>
          {t("cargoInsurance.territories.listTitle")}
        </p>
        <ul className={styles.list} aria-labelledby={listTitleId}>
          {territoryKeys.map((key) => (
            <li key={key}>
              <LocationIconNew width={24} height={24} aria-hidden="true" />
              <span>{t(`cargoInsurance.territories.countries.${key}`)}</span>
            </li>
          ))}
        </ul>
        <div className={styles.notice}>
          <Info size={24} aria-hidden="true" />
          <p>{t("cargoInsurance.territories.notice")}</p>
        </div>
        <div className={styles.footer}>
          <button type="button" className={styles.confirm} onClick={onClose}>
            {t("cargoInsurance.understood")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
